import React, { useEffect, useCallback, useRef, useState } from 'react';
import PageContainer from '../layouts/PageContainer';
import PageHeader from '../components/Nav/PageHeader';
import StatsCardRow from '../components/Dashboard/StatsCardRow';
import RevenueTrendsChart from '../components/Dashboard/RevenueTrendsChart';
import PopularPlansChart from '../components/Dashboard/PopularPlansChart';
import StarterPanel from '../components/Dashboard/StarterPanel';
import WifiMetricsRow from '../components/Dashboard/WifiMetricsRow';
import DashboardMetrics from '../components/Dashboard/DashboardMetrics';
import RecentWifiSessions from '../components/Dashboard/RecentWifiSessions';
import { useStat } from '../DataStore/StatContext';
import { useVoucher } from '../DataStore/VoucherContext';
import { useNavigate } from 'react-router-dom';

const REFRESH_MS =
  Number(process.env.REACT_APP_DASH_REFRESH_MS || 30000); // 30s default

const DashboardPage = () => {
  const {
    getRevenueTrends, revenueTrends,
    getRevenueCharts, revenueCharts,
    getPopularPlansChart, popularPlansChart,
    getUserMetrics, userMetrics
  } = useStat();
  const navigation = useNavigate();

  const { getRecentVoucherActivity, recentVoucherActivity, getTrafficUsage, trafficUsage } = useVoucher();

  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchingRef = useRef(false);

  const fetchAll = useCallback(async (force = false) => {
    if (fetchingRef.current) return; // avoid overlap
    if (document.visibilityState !== 'visible') return; // skip hidden tabs
    if (typeof navigator !== 'undefined' && !navigator.onLine) return; // skip offline

    fetchingRef.current = true;
    try {
      await Promise.all([
        getRevenueTrends(force),
        getRevenueCharts(force),
        getPopularPlansChart(force),
        getUserMetrics(force),
        getRecentVoucherActivity(force),
        getTrafficUsage(force)
      ]);
      setLastUpdated(new Date());
    } finally {
      fetchingRef.current = false;
    }
  }, [
    getRevenueTrends,
    getRevenueCharts,
    getPopularPlansChart,
    getUserMetrics,
    getRecentVoucherActivity,
    getTrafficUsage
  ]);

  // Initial load
  useEffect(() => { fetchAll(false); }, [fetchAll]);

  // Polling + focus/online triggers
  useEffect(() => {
    const tick = () => fetchAll(true);

    const id = setInterval(tick, REFRESH_MS);
    window.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    window.addEventListener('online', tick);

    return () => {
      clearInterval(id);
      window.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
      window.removeEventListener('online', tick);
    };
  }, [fetchAll]);

  const handleRefresh = () => fetchAll(true);

  const handleSessionClick = (deviceToken) => {
    navigation(`/network-device?m=${deviceToken}`);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        breadcrumb={<li className="breadcrumb-item active" aria-current="page">Dashboard</li>}
        primaryButtons={[
          {
            label: 'Refresh',
            icon: 'fas fa-sync',
            className: 'btn-outline-secondary',
            onClick: handleRefresh
          }
        ]}
        subtitle={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : undefined}
      />

      <div className="row">
        <StarterPanel />
        <div className="col-lg-8 mb-4"><WifiMetricsRow trafficUsage={trafficUsage} /></div>
      </div>

      <div className="row row-sm">
        <div className="col-lg-6">
          <div className="row row-sm">
            <StatsCardRow revenueTrends={revenueTrends} />
          </div>
          <div className="row row-sm">
            <div className="col-lg-12">
              <RevenueTrendsChart revenueData={revenueCharts} />
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="row row-sm">
            <div className="col-lg-8">
              <PopularPlansChart popularPlansData={popularPlansChart} />
              <RecentWifiSessions sessions={recentVoucherActivity} goToProfile={handleSessionClick} />
            </div>
            <div className="col-lg-4">
              <DashboardMetrics userMetrics={userMetrics} />
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default DashboardPage;
